import * as React from "react";

import PeriodicUpdate from "./periodic-update.tsx";

/**
 * This component wraps the PeriodicUpdate component and creates a React
 * Context. This Context provides a function that allows components further down
 * in the tree to enable and disable the "forceRefresh" functionality of the
 * PeriodicUpdate component. This is useful if there are components that do
 * asynchronous work that would be disrupted by reloading the page. For example,
 * if you sync client state to the server in the background, that sync could be
 * interrupted by forcing a full page refresh on navigation, and data loss
 * could occur.
 *
 * The easiest way to use this functionality is with the
 * withPeriodicUpdateControls Higher Order Component. See that component for
 * more details on usage.
 */

export type PeriodicUpdateContextType = Readonly<{
    /**
     * Set the allowForceRefresh option on the PeriodicUpdate component. If
     * set to true, client-side navigations may be turned into into full page
     * refreshes. If it is set to false, client-side navigations will never
     * cause full page refreshes.
     */
    toggleForceRefresh: (allowForceRefresh: boolean) => void;
}>;

export const PeriodicUpdateContext: React.Context<PeriodicUpdateContextType> =
    React.createContext<PeriodicUpdateContextType>({
        toggleForceRefresh: (allowForceRefresh) => {},
    });
PeriodicUpdateContext.displayName = "PeriodicUpdateContext";

type PeriodicUpdateContainerProps = {
    children: JSX.LibraryManagedAttributes<
        typeof PeriodicUpdate,
        React.ComponentProps<typeof PeriodicUpdate>
    >["children"];
};

export default function PeriodicUpdateContainer({
    children,
}: PeriodicUpdateContainerProps): React.ReactElement {
    const [allowForceRefresh, setAllowForceRefresh] = React.useState(true);

    const contextValue = React.useMemo(
        () => ({toggleForceRefresh: setAllowForceRefresh}),
        [],
    );

    return (
        <PeriodicUpdateContext.Provider value={contextValue}>
            <PeriodicUpdate allowForceRefresh={allowForceRefresh}>
                {children}
            </PeriodicUpdate>
        </PeriodicUpdateContext.Provider>
    );
}
